import { useSoftphone } from "@/contexts/SoftphoneContext";
import { faPhoneVolume } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";

/**
 * Empêche de recharger la page pendant un appel : le softphone vit dans la
 * page, un rafraîchissement coupe donc la communication en cours.
 *
 * Deux protections, parce qu'une seule ne peut pas tout couvrir :
 *
 * - Le raccourci clavier (F5, Ctrl+R, Cmd+R), de loin le cas courant, est
 *   intercepté et remplacé par la modale ci-dessous, qui explique pourquoi.
 * - Le bouton Actualiser du navigateur et la fermeture d'onglet ne peuvent pas
 *   l'être : les navigateurs n'y autorisent que leur propre dialogue « Quitter
 *   le site ? », dont le texte n'est pas personnalisable. C'est une protection
 *   anti-abus, aucune application ne peut l'outrepasser. `beforeunload` sert
 *   donc de filet, avec le message générique du navigateur.
 */
const PreventRefreshWhileOnCall: FC = () => {
  const { callState, incomingCall } = useSoftphone();
  const [showWarning, setShowWarning] = useState(false);

  const isOnCall = callState !== "idle" || !!incomingCall;

  useEffect(() => {
    if (!isOnCall) {
      setShowWarning(false);
      return;
    }

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Certains navigateurs anciens exigent encore returnValue pour déclencher
      // le dialogue ; sa valeur n'est jamais affichée.
      event.returnValue = "";
      return "";
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const isReloadShortcut =
        event.key === "F5" ||
        ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "r");

      if (!isReloadShortcut) return;

      event.preventDefault();
      setShowWarning(true);
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOnCall]);

  return (
    <Modal
      show={showWarning}
      onHide={() => setShowWarning(false)}
      centered
      size="sm"
    >
      <Modal.Header closeButton>
        <Modal.Title className="fs-0 d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faPhoneVolume} className="text-danger" />
          Appel en cours
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-0 text-800">
          Impossible de rafraîchir le site pendant que vous êtes en ligne :
          l'appel serait immédiatement coupé.
        </p>
        <p className="mb-0 mt-2 text-600 fs--1">
          Raccrochez d'abord, puis rechargez la page.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={() => setShowWarning(false)}>
          J'ai compris
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PreventRefreshWhileOnCall;
